import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Button, Input } from '../../../components/shared/StyledComponents'
import { api, LinkedInProjectDetail, LinkedInProjectSummary } from '../../../services/api'
import { AssignTarget } from './AssignTaskModal'
import { ProjectFormModal, PracticasProjectModal, apiError } from './ProjectModals'
import { Alert, Chip } from './linkedinStyles'

/**
 * SOTA projects: bundles of repos, papers, publications and technologies that are
 * studied, and assigned, as one piece of work. Everyone reads them; admins create,
 * edit, assign and delete. The same operations are available to the chatbot and
 * to MCP clients (see docs/LINKEDIN_BOT.md).
 */

const Layout = styled.div`
    display: grid;
    grid-template-columns: minmax(240px, 340px) 1fr;
    gap: ${({ theme }) => theme.spacing.md};

    @media (max-width: 820px) {
        grid-template-columns: 1fr;
    }
`

const Bar = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: ${({ theme }) => theme.spacing.md};
`

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`

const Row = styled.button<{ $active: boolean }>`
    text-align: left;
    border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: 0.7rem 0.9rem;
    cursor: pointer;
    font: inherit;

    &:hover {
        border-color: ${({ theme }) => theme.colors.primary};
    }
`

const Panel = styled.div`
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    padding: 1rem 1.2rem;
`

const Counts = styled.div`
    font-size: 0.78rem;
    opacity: 0.75;
    font-variant-numeric: tabular-nums;
`

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    margin: 0.4rem 0;
`

const Section = styled.h4`
    margin: 1rem 0 0.4rem;
`

const Item = styled.div`
    display: flex;
    justify-content: space-between;
    gap: 0.6rem;
    padding: 0.35rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    font-size: 0.88rem;

    &:last-child {
        border-bottom: none;
    }
`

const LinkButton = styled.button`
    border: none;
    background: none;
    padding: 0;
    font: inherit;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    text-decoration: underline;
`

const Empty = styled.p`
    opacity: 0.75;
`

interface Props {
    isAdmin: boolean
    /** Opens the shared detail modal for a repo / paper / post. */
    onOpenItem: (type: 'repo' | 'paper' | 'post', id: number) => void
    /** Starts the assignment modal on a project. */
    onAssign?: (target: AssignTarget) => void
    /** Bumped by the page when an assignment was saved, so the open project refetches. */
    reloadKey?: number
}

export const ProjectsView: React.FC<Props> = ({ isAdmin, onOpenItem, onAssign, reloadKey = 0 }) => {
    const [projects, setProjects] = useState<LinkedInProjectSummary[]>([])
    const [selected, setSelected] = useState<LinkedInProjectDetail | null>(null)
    const [query, setQuery] = useState('')
    const [loading, setLoading] = useState(true)
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
    const [form, setForm] = useState<'none' | 'create' | 'edit' | 'practicas'>('none')

    const loadList = useCallback(async (search: string) => {
        try {
            const res = await api.linkedin.getProjects(search.trim() || undefined)
            setProjects(res.data)
        } catch {
            setMessage({ type: 'error', text: 'No se pudieron cargar los proyectos.' })
        } finally {
            setLoading(false)
        }
    }, [])

    const open = useCallback(async (id: number) => {
        try {
            const res = await api.linkedin.getProject(id)
            setSelected(res.data)
        } catch {
            setMessage({ type: 'error', text: 'No se pudo cargar el proyecto.' })
        }
    }, [])

    useEffect(() => {
        const handle = setTimeout(() => void loadList(query), query ? 300 : 0)
        return () => clearTimeout(handle)
    }, [query, loadList, reloadKey])

    // An assignment saved from the modal changes who is on the open project.
    useEffect(() => {
        if (reloadKey > 0 && selected) void open(selected.id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadKey])

    const saved = (project: LinkedInProjectDetail, text: string) => {
        setForm('none')
        // The create/update answer has no assignments: read the full detail.
        void open(project.id)
        setMessage({ type: 'success', text })
        void loadList(query)
    }

    const remove = async () => {
        if (!selected) return
        if (!window.confirm(`¿Eliminar el proyecto «${selected.name}»? Los repos, papers y publicaciones no se tocan.`)) return
        try {
            await api.linkedin.deleteProject(selected.id)
            setSelected(null)
            setMessage({ type: 'success', text: 'Proyecto eliminado.' })
            void loadList(query)
        } catch (err) {
            setMessage({ type: 'error', text: apiError(err, 'No se pudo eliminar el proyecto.') })
        }
    }

    if (loading) return <Empty>Cargando proyectos…</Empty>

    return (
        <>
            {message && <Alert $type={message.type}>{message.text}</Alert>}
            <Bar>
                <Input
                    value={query}
                    aria-label="Buscar proyectos"
                    placeholder="Buscar por nombre, descripción o tecnología…"
                    style={{ maxWidth: 380 }}
                    onChange={(e) => setQuery(e.target.value)}
                />
                {isAdmin && (
                    <>
                        <Button type="button" variant="primary" onClick={() => setForm('create')}>
                            + Nuevo proyecto
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => setForm('practicas')}>
                            🎓 Proyecto de prácticas
                        </Button>
                    </>
                )}
            </Bar>

            {projects.length === 0 ? (
                <Empty>
                    {query
                        ? 'Ningún proyecto coincide con la búsqueda.'
                        : 'Todavía no hay proyectos. Un proyecto agrupa repos, papers, publicaciones y tecnologías para estudiarlos y asignarlos juntos.'}
                </Empty>
            ) : (
                <Layout>
                    <List>
                        {projects.map((p) => (
                            <Row key={p.id} type="button" $active={selected?.id === p.id} onClick={() => open(p.id)}>
                                <strong>{p.user_id ? '🎓 ' : ''}{p.name}</strong>
                                <Counts>
                                    {p.repo_count} repos · {p.paper_count} papers · {p.post_count} publicaciones
                                    {p.active_assignments > 0 ? ` · ${p.active_assignments} asignados` : ''}
                                </Counts>
                            </Row>
                        ))}
                    </List>

                    <Panel>
                        {!selected ? (
                            <Empty>Selecciona un proyecto.</Empty>
                        ) : (
                            <>
                                <h3 style={{ marginTop: 0 }}>{selected.name}</h3>
                                {selected.description && <p style={{ whiteSpace: 'pre-wrap' }}>{selected.description}</p>}
                                <Chips>
                                    {(selected.technologies ?? []).map((t) => (
                                        <Chip key={t} $bg="#e8f5e9" $color="#1b5e20">
                                            {t}
                                        </Chip>
                                    ))}
                                    {selected.redmine_project_id && (
                                        <Chip $bg="#e3f2fd" $color="#0d47a1" title={selected.redmine_project_name || ''}>
                                            🔗 Redmine: {selected.redmine_identifier || selected.redmine_project_id}
                                        </Chip>
                                    )}
                                </Chips>

                                {isAdmin && (
                                    <Bar>
                                        <Button type="button" variant="secondary" size="sm" onClick={() => setForm('edit')}>
                                            ✏️ Editar
                                        </Button>
                                        {onAssign && (
                                            <Button
                                                type="button"
                                                variant="primary"
                                                size="sm"
                                                onClick={() =>
                                                    onAssign({ target_type: 'project', target_id: selected.id, target_title: selected.name })
                                                }
                                            >
                                                🎯 Asignar
                                            </Button>
                                        )}
                                        <Button type="button" variant="danger" size="sm" onClick={remove}>
                                            🗑️ Eliminar
                                        </Button>
                                    </Bar>
                                )}

                                <Section>Repositorios ({(selected.repos ?? []).length})</Section>
                                {(selected.repos ?? []).map((r) => (
                                    <Item key={r.id}>
                                        <LinkButton type="button" onClick={() => onOpenItem('repo', r.id)}>
                                            {r.owner}/{r.name}
                                        </LinkButton>
                                        <span style={{ opacity: 0.75 }}>
                                            {r.language || ''}
                                            {typeof r.stars === 'number' ? ` · ⭐ ${r.stars}` : ''}
                                            {r.url && (
                                                <>
                                                    {' · '}
                                                    <a href={r.url} target="_blank" rel="noopener noreferrer">
                                                        ↗
                                                    </a>
                                                </>
                                            )}
                                        </span>
                                    </Item>
                                ))}
                                <Section>Papers ({(selected.papers ?? []).length})</Section>
                                {(selected.papers ?? []).map((p) => (
                                    <Item key={p.id}>
                                        <LinkButton type="button" onClick={() => onOpenItem('paper', p.id)}>
                                            {p.title || p.external_id || p.url}
                                        </LinkButton>
                                        <a href={p.url} target="_blank" rel="noopener noreferrer">
                                            ↗
                                        </a>
                                    </Item>
                                ))}
                                <Section>Publicaciones ({(selected.posts ?? []).length})</Section>
                                {(selected.posts ?? []).map((p) => (
                                    <Item key={p.id}>
                                        <LinkButton type="button" onClick={() => onOpenItem('post', p.id)}>
                                            {p.author || 'Autor desconocido'} · {(p.summary || 'Sin resumen').slice(0, 90)}
                                        </LinkButton>
                                    </Item>
                                ))}
                                <Section>Asignados ({(selected.assignments ?? []).length})</Section>
                                {(selected.assignments ?? []).length === 0 ? (
                                    <Counts>Nadie tiene asignado este proyecto.</Counts>
                                ) : (
                                    (selected.assignments ?? []).map((a) => (
                                        <Item key={a.id}>
                                            <span>{a.user_display_name || `usuario #${a.user_id}`}</span>
                                            <span style={{ opacity: 0.75 }}>
                                                {a.estado}
                                                {a.fecha_fin ? ` · hasta ${a.fecha_fin}` : ''}
                                            </span>
                                        </Item>
                                    ))
                                )}
                            </>
                        )}
                    </Panel>
                </Layout>
            )}

            {form === 'create' && (
                <ProjectFormModal onClose={() => setForm('none')} onSaved={(p) => saved(p, 'Proyecto creado.')} />
            )}
            {form === 'edit' && selected && (
                <ProjectFormModal
                    project={selected}
                    onClose={() => setForm('none')}
                    onSaved={(p) => saved(p, 'Proyecto actualizado.')}
                />
            )}
            {form === 'practicas' && (
                <PracticasProjectModal onClose={() => setForm('none')} onSaved={(p) => saved(p, `Creado ${p.name}.`)} />
            )}
        </>
    )
}

export default ProjectsView
